import type {
  LabelSchema,
  LocationSchema,
  MembershipSchema,
  TeamSchema,
  WeeklyScheduleSchema
} from '@libs/data-access/entities'
import type {
  AccountSchema,
  ScheduleMembershipSchema,
  ShiftRQSchema
} from '@libs/data-access/queries'
import { contractService, queryClientOptions } from '@libs/data-access/queries'
import { Alert, AlertErrorAdapter, Modal, usePrevious } from '@libs/ui/ds'
import { BaseForm } from '@libs/ui/form'
import { usePlanningFormTranslation } from '@libs/utils/translations'
import {
  LabelSelect,
  LockedAlert
} from '@spa/scenes/Plannings/Forms/components'
import moment from 'moment'
import React, { useEffect, useMemo, useState } from 'react'
import type { FC } from 'react'
import { getRoleRights } from 'snap-redux/modules/memberships/utils'

import styles from './ShiftFormLegacy.module.css'
import {
  AutomaticBreakRowsContainer,
  LocationAndTeamFields,
  NotifyUser,
  ShiftActions,
  ShiftNote,
  ShiftTimeRangeInputs,
  UpdateDayField,
  UserContractField,
  WeekdaysFields
} from './components'
import { MealRowsContainerLegacy } from './components/MealRowsContainer/MealRowsContainerLegacy'
import { ShowMore } from './components/ShowMore/ShowMore'
import { useShiftForm } from './hooks'
import { useDefaultLabel } from './hooks/useDefaultLabel'
import { useDisplayEndContractOverrunWarning } from './hooks/useDisplayEndContractOverrunWarning'
import { useFloatingBreaksWarning } from './hooks/useDisplayFloatingBreaksWarning'
import { getDefaultShiftModel } from './utils'
import { UNASSIGNED_SHIFT } from './utils/createShiftArrayPayload'

type Props = {
  onFormSuccess: () => void
  date?: string | Date
  userContractId?: number
  labelId?: number
  currentAccount: AccountSchema
  currentUserMembership?: MembershipSchema
  currentWeeklySchedule: WeeklyScheduleSchema
  currentWeeklyScheduleLocation: LocationSchema
  weeklySchedules: WeeklyScheduleSchema[]
  currentShift?: ShiftRQSchema
  currentShiftMembership?: ScheduleMembershipSchema
  currentShiftTeam?: TeamSchema
  allAccountTeams: TeamSchema[]
  allAccountLocations: LocationSchema[]
  allLabels: LabelSchema[]
}

export const ShiftForm: FC<Props> = ({
  onFormSuccess,
  date,
  userContractId,
  labelId,
  currentAccount,
  currentUserMembership,
  weeklySchedules,
  currentWeeklySchedule,
  currentWeeklyScheduleLocation,
  currentShift,
  currentShiftMembership,
  currentShiftTeam,
  allAccountTeams,
  allAccountLocations,
  allLabels
}) => {
  const { t } = usePlanningFormTranslation()
  const defaultLabel = useDefaultLabel()

  const [showingMore, setShowingMore] = useState(false)

  const { id: currentShiftId } = currentShift ?? { id: undefined }

  const {
    id: currentWeeklyScheduleId,
    published: weeklyScheduleIsPublished,
    team_id: weeklyScheduleTeamId
  } = currentWeeklySchedule

  // Maybe move it to the container... don't know yet!
  const shift =
    currentShift ||
    getDefaultShiftModel({
      userContractId,
      weeklyscheduleId: currentWeeklyScheduleId,
      date,
      defaultMeal: currentAccount.meal_by_default_for_real
    })

  // This is in redux folder but it's just a helper function, so we can keep it
  const { atLeast } = getRoleRights(currentUserMembership)

  const isCreation = !shift?.id

  const [teamId, setTeamId] = useState(
    currentShiftTeam?.id || weeklyScheduleTeamId
  )

  const methods = useShiftForm({
    currentAccount,
    currentMembership: currentShiftMembership,
    labelId,
    shift,
    teamId,
    weeklyScheduleIsPublished,
    weeklyscheduleLocation: currentWeeklyScheduleLocation,
    weeklySchedules,
    onFormSuccess
  })

  const {
    watch,
    setValue,
    onSubmit,
    formError,
    control,
    formState: { errors },
    formLoading,
    onDeleteSubmit
  } = methods

  const userContracts = watch('user_contract')
  const selectedDays = watch('days')
  const selectedTeamId = Number(watch('team')?.value) || teamId
  const selectedLocationId =
    Number(watch('location')?.value) || currentWeeklyScheduleLocation.id
  const selectedContractId =
    Number(userContracts?.[0]?.value) || shift?.user_contract_id

  // Depending on the first contract, we refetch. Will be redone in the future because we can have many employees selected but we only check on the first one...
  const { data: contract } = contractService.contract.useFetchOne(
    {
      pathParams: {
        user_contract_id: selectedContractId
      }
    },
    {
      enabled:
        Boolean(selectedContractId) && selectedContractId !== UNASSIGNED_SHIFT,
      staleTime: queryClientOptions.time.STATIC
    }
  )

  const isUpdatingContract = selectedContractId !== shift?.user_contract_id

  const handleToggleLocation = () => setShowingMore(!showingMore)

  // @ts-ignore team_ids still use Immutable... so typing is sh**...
  const selectedContractTeams: TeamSchema[] =
    contract?.team_ids
      .map(teamId =>
        teamId ? allAccountTeams.find(team => team.id === teamId) : undefined
      )
      .filter(x => Boolean(x)) ?? []

  let accessibleTeamsLocations =
    selectedContractTeams
      .map(team =>
        allAccountLocations.find(location => location.id === team.location_id)
      )
      .filter(x => !!x) ?? []
  if (selectedContractTeams.length === 0) {
    // If no contract are selected, we only display the list on employee in the current location/team filter
    accessibleTeamsLocations =
      allAccountLocations.filter(
        location =>
          location.id === selectedLocationId &&
          location.teams.map(team => team.id).includes(selectedTeamId)
      ) ?? []
  }

  const labelsForSelectedTeam = allLabels.filter(
    label => label.team_id === teamId
  )

  useEffect(() => {
    const team = selectedContractTeams.find(
      ({ location_id: teamLocationId }) => teamLocationId === selectedLocationId
    )
    // We should only update the team IF the location is changed as well.
    if (team && currentWeeklyScheduleLocation.id !== selectedLocationId) {
      setValue(
        'team',
        { value: team?.id, label: team?.name },
        { shouldDirty: true }
      )
      setTeamId(team?.id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocationId])

  useEffect(() => {
    setTeamId(selectedTeamId)
  }, [selectedTeamId])

  const prevTeamId = usePrevious(teamId)
  useEffect(() => {
    if (prevTeamId) {
      setValue('label', defaultLabel, { shouldDirty: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId])

  const shiftStartDate = moment(shift.starts_at).format('YYYY-MM-DD')

  const isShiftLocked = Boolean(shift?.locked_at)

  const isUpdatingWeeklyschedule = useMemo(() => {
    const nextWeeklyschedule = weeklySchedules.find(
      weeklyschedule => weeklyschedule?.team_id === teamId
    )
    return Boolean(
      shift &&
        nextWeeklyschedule &&
        nextWeeklyschedule?.id !== shift?.weeklyschedule_id
    )
  }, [weeklySchedules, teamId, shift])

  const breakArePaid = currentAccount.paid_breaks

  const haveMultipleUserWithError = userContracts.filter(
    option => option?.highlight
  ).length

  const displayFloatingBreaksWarning = useFloatingBreaksWarning({
    contractId: contract?.id,
    date: selectedDays[0]?.value,
    shiftTimeRange: watch('shift_time_range'),
    breaks: watch('breaks')
  })

  const displayEndContractOverrunWarning = useDisplayEndContractOverrunWarning({
    contractEnd: contract?.contract_end,
    date: selectedDays[0]?.value,
    shiftTimeRange: watch('shift_time_range')
  })

  // Used at multiple place
  const LabelFieldComponent = (
    <LabelSelect
      control={control}
      errors={errors}
      disabled={Boolean(shift.locked_at)}
      haveAccess={atLeast.director}
      labels={labelsForSelectedTeam}
      selectedLocationId={selectedLocationId}
      setValue={setValue}
    />
  )

  /** Save action disabled handler, split for better visibility */
  const saveActionDisabled =
    formLoading || !selectedDays?.length || displayEndContractOverrunWarning

  let saveActionUnassignedDisabled = false
  if (
    userContracts?.length > 0 &&
    userContracts?.length === haveMultipleUserWithError
  ) {
    saveActionUnassignedDisabled = true
  }

  const displayShowMore =
    (accessibleTeamsLocations.length > 1 || selectedContractTeams.length > 1) &&
    !isCreation

  return (
    <BaseForm
      methods={methods}
      onSubmit={onSubmit}
      className='flex grow flex-col'
    >
      <Modal.Content>
        {isShiftLocked && <LockedAlert />}
        {formError && <AlertErrorAdapter className='mb-5' error={formError} />}
        <UserContractField
          control={control}
          errors={errors}
          disabled={Boolean(shift?.locked_at) || isUpdatingWeeklyschedule}
          isUpdatingWeeklyschedule={isUpdatingWeeklyschedule}
          shiftDateISO={shiftStartDate}
          weeklyScheduleTeamId={weeklyScheduleTeamId}
          haveMultipleUserWithError={haveMultipleUserWithError}
          isCreation={isCreation}
          valueState={userContracts}
          setValue={setValue}
          selectedDays={selectedDays.map(day => day.value)}
        />
        {!isCreation && (
          <UpdateDayField
            weeklyschedule={currentWeeklySchedule}
            shift={shift}
            control={control}
            errors={errors}
            setValue={setValue}
          />
        )}
        {isCreation && (
          <WeekdaysFields
            selectedDays={selectedDays}
            control={control}
            errors={errors}
            setValue={setValue}
            date={date}
          />
        )}
        <div className={styles.MainGroup}>
          {(!showingMore || isUpdatingContract) && LabelFieldComponent}
          <ShiftTimeRangeInputs
            control={control}
            errors={errors}
            disabled={isShiftLocked}
          />
          {displayEndContractOverrunWarning && (
            <Alert
              intent='semantic.red'
              switchAt='sm'
              title={t('component.endContractOverrunWarning.title')}
              className='mb-4'
            >
              {t('component.endContractOverrunWarning.warning')}
            </Alert>
          )}
          <AutomaticBreakRowsContainer
            isShiftLocked={isShiftLocked}
            isBreakPaid={breakArePaid}
            isManager={atLeast.manager}
            location={currentWeeklyScheduleLocation}
            displayFloatingBreaksWarning={displayFloatingBreaksWarning}
            shiftId={currentShiftId}
          />
          <MealRowsContainerLegacy
            control={control}
            defaultSelectedOption={
              currentWeeklyScheduleLocation?.default_meal_type
            }
            isShiftLocked={isShiftLocked}
          />
          <ShiftNote
            control={control}
            errors={errors}
            isShiftLocked={isShiftLocked}
          />
        </div>
        {weeklyScheduleIsPublished && (
          <NotifyUser
            control={control}
            errors={errors}
            isDisabled={Boolean(shift?.locked_at)}
          />
        )}
        {displayShowMore && (
          <ShowMore onClick={handleToggleLocation} showingMore={showingMore} />
        )}
        {showingMore && (
          <>
            <LocationAndTeamFields
              locations={accessibleTeamsLocations}
              teams={selectedContractTeams}
              disabled={isShiftLocked || isUpdatingContract}
              selectedLocationId={selectedLocationId}
              control={control}
              errors={errors}
              setValue={setValue}
            />
            {isUpdatingContract && (
              <Alert intent='semantic.blue'>
                {t('shiftForm.trickModifyEmployee')}
              </Alert>
            )}
            {!isUpdatingContract && LabelFieldComponent}
          </>
        )}
      </Modal.Content>
      <Modal.Footer className='sticky bottom-0 bg-neutral-50 pt-6'>
        {!isShiftLocked && (
          <ShiftActions
            shiftId={shift.id}
            formLoading={formLoading}
            onClickDelete={() =>
              onDeleteSubmit({ notify_user: watch('notify_user') })
            }
            createDisabled={saveActionDisabled || saveActionUnassignedDisabled}
            isCreation={isCreation}
          />
        )}
      </Modal.Footer>
    </BaseForm>
  )
}
